import React, { useEffect, useState, useCallback } from "react";
import { lineString } from "@turf/helpers";
import Planner, { useWindowHeight } from "./Planner";
import { useViewport, createFitBounds } from "../route-explorer/MapContainer";
import { useBreakpointsContext } from "../../hooks";
import { useStopsById, useStopIdsByPlaceStartId } from "../route-explorer/hooks";
import { connect, getDefaultIsAutoFollowActive } from "../../store";
import { useTranslate } from "../../translate";
import { getItineraryCardsTopOffset } from "../itinerary/ItineraryContainer";
import { MAP_HEIGHT_MOBILE, APP_HEADER_MOBILE, APP_HEADER } from "../../constants";
import { useMapOverlay } from "../route-explorer/Map";
import { useAutoFollow } from "./hooks";

function PlannerContainer(props) {
    const {
        dispatch,
        itineraryId,
        enableChangeItinerary,
        onItineraryIdChange,
        routeItineraries,
        inLauncherModal,
        inPdfMode,
    } = props;
    const { isMobile, isTablet, isExtraSmall } = useBreakpointsContext();
    const useTwoColumnLayout = !isExtraSmall;
    const translate = useTranslate();
    const [viewport, setViewport] = useViewport();
    const [itinerary, setItinerary] = useState();
    const appHeaderHeight = isMobile || isTablet ? APP_HEADER_MOBILE : APP_HEADER;
    let dayTopOffset = appHeaderHeight;
    if (useTwoColumnLayout) {
        dayTopOffset = appHeaderHeight;
    } else {
        dayTopOffset = appHeaderHeight + MAP_HEIGHT_MOBILE;
    }
    const onFetchItinerary = itinerary => setItinerary(itinerary);
    const [isAutoFollowEnabled, setIsAutoFollowEnabled] = useState(getDefaultIsAutoFollowActive());
    const [isSecondWayVisible, setIsSecondWayVisible] = useState(false);
    const [activeStopId, setActiveStopId] = useState();
    const [isMetadataInScrollView, setIsMetadataInScrollView] = useState();
    const [activePlaceStartId, setActivePlaceStartId] = useState();
    const stopsById = useStopsById(itinerary);
    const stopIdsByPlaceStartId = useStopIdsByPlaceStartId(itinerary);
    const windowHeight = useWindowHeight();
    const [loopDirection, setLoopDirection] = useState("CLOCKWISE");
    const [startingPoint, setStartingPoint] = useState(null);
    const { showMapOverlay, mapOverlayText, isMapOverlayVisible } = useMapOverlay();
    const [sizeByStickyContent, setSizeByStickyContent] = useState({
        dayComputedHeight: 0,
        mapComputedHeight: 0,
        mapWrapperComputedHeight: 0,
    });
    const _setLoopDirection = value => {
        setLoopDirection(value);
        showMapOverlay("Changing the itinerary direction");
    };
    const _onItineraryIdChange = value => {
        onItineraryIdChange(value);
        showMapOverlay("Changing the itinerary");
    };
    const cardsOffsetTop = getItineraryCardsTopOffset({
        useOldLayout: false,
        useTwoColumnLayout,
        windowHeight,
        isMobile,
        isTablet,
        itinerary,
        inPlanner: true,
        sizeByStickyContent,
    });
    const onMarkerClick = useCallback(
        id => {
            let query = `.js__stop-card[data-stop-id="${id}"]`;
            const cardEl = document.querySelector(query);
            if (!cardEl) {
                return;
            }
            const cardElY = cardEl.getBoundingClientRect().y;
            window.scrollBy({
                left: 0,
                behavior: "smooth",
                top: cardElY - cardsOffsetTop,
            });
        },
        [cardsOffsetTop],
    );

    //Re center map when scrolling stops
    useEffect(() => {
        if (!activeStopId || !isAutoFollowEnabled) {
            return;
        }
        if (stopsById[activeStopId].type === "place_start") {
            try {
                if (stopIdsByPlaceStartId[activeStopId].length >= 2) {
                    const geojsonObject = lineString(
                        stopIdsByPlaceStartId[activeStopId].map(stopId => [
                            stopsById[stopId].coords.lon,
                            stopsById[stopId].coords.lat,
                        ]),
                    );
                    const fitBounds = createFitBounds(geojsonObject);
                    const mapboxEl = document.querySelector(".js__mapbox-gl");
                    if (!mapboxEl) {
                        return;
                    }
                    const mapSize = mapboxEl.getBoundingClientRect();
                    fitBounds({ mapSize, onViewportChange: setViewport, useTwoColumnLayout, useOldLayout: false });
                } else if (stopIdsByPlaceStartId[activeStopId].length === 1) {
                    const stopId = stopIdsByPlaceStartId[activeStopId];
                    const stop = stopsById[stopId];
                    setViewport({
                        latitude: stop.coords.lat,
                        longitude: stop.coords.lon,
                        zoom: 10,
                        manuallyChanged: false,
                    });
                }
            } catch (e) {
                console.warn(e);
            }
        } else {
            setViewport({
                latitude: stopsById[activeStopId].coords.lat,
                longitude: stopsById[activeStopId].coords.lon,
                zoom: 10,
                manuallyChanged: false,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStopId]);

    const { activeSuggestionId, setActiveSuggestionId } = useAutoFollow({
        isAutoFollowEnabled,
        stopsById,
        createFitBounds,
        setViewport,
        useTwoColumnLayout,
    });

    //Re center map When scrolling to the top
    useEffect(() => {
        if (!isAutoFollowEnabled) {
            return;
        }
        const geojsonObject = itinerary?.routeData?.routePath;
        if (!isMetadataInScrollView || !geojsonObject) {
            return;
        }
        const fitBounds = createFitBounds(geojsonObject);
        const mapboxEl = document.querySelector(".js__mapbox-gl");
        if (!mapboxEl) {
            return;
        }
        const mapSize = mapboxEl.getBoundingClientRect();
        fitBounds({ mapSize, onViewportChange: setViewport, useTwoColumnLayout, useOldLayout: false });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMetadataInScrollView]);

    //Show "place name" in day header
    useEffect(() => {
        if (!activePlaceStartId) {
            dispatch({
                type: "SET_CURRENT_PLACE_NAME",
                payload: "",
            });
            return;
        }
        dispatch({
            type: "SET_CURRENT_PLACE_NAME",
            payload: translate(stopsById[activePlaceStartId], "name"),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activePlaceStartId]);

    return (
        <Planner
            stopsById={stopsById}
            isMapOverlayVisible={isMapOverlayVisible}
            mapOverlayText={mapOverlayText}
            viewport={viewport}
            setViewport={setViewport}
            onFetchItinerary={onFetchItinerary}
            itinerary={itinerary}
            isSecondWayVisible={isSecondWayVisible}
            setIsSecondWayVisible={setIsSecondWayVisible}
            activeStopId={activeStopId}
            setActiveStopId={setActiveStopId}
            isMetadataInScrollView={isMetadataInScrollView}
            setIsMetadataInScrollView={setIsMetadataInScrollView}
            activePlaceStartId={activePlaceStartId}
            setActivePlaceStartId={setActivePlaceStartId}
            stopIdsByPlaceStartId={stopIdsByPlaceStartId}
            isAutoFollowEnabled={isAutoFollowEnabled}
            setIsAutoFollowEnabled={setIsAutoFollowEnabled}
            loopDirection={loopDirection}
            setLoopDirection={_setLoopDirection}
            onMarkerClick={onMarkerClick}
            itineraryId={itineraryId}
            dayTopOffset={dayTopOffset}
            appHeaderHeight={appHeaderHeight}
            enableChangeItinerary={enableChangeItinerary}
            onItineraryIdChange={_onItineraryIdChange}
            onStartingPointChange={setStartingPoint}
            startingPoint={startingPoint}
            routeItineraries={routeItineraries}
            inLauncherModal={inLauncherModal}
            useTwoColumnLayout={useTwoColumnLayout}
            sizeByStickyContent={sizeByStickyContent}
            setSizeByStickyContent={setSizeByStickyContent}
            cardsOffsetTop={cardsOffsetTop}
            inPdfMode={inPdfMode}
            activeSuggestionId={activeSuggestionId}
            setActiveSuggestionId={setActiveSuggestionId}
        />
    );
}

const mapStateToProps = () => ({});

export default connect(mapStateToProps)(PlannerContainer);
